import { useMemo, useState } from 'react';
import { Alert, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useEntityTasks, useCompleteTask } from '@/hooks/taskmanagement/useTaskInbox';
import { useHasPermission } from '@/components/PermissionGate';
import { authService } from '@/services/auth/authService';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import type { WorkTask } from '@/types/taskmanagement/WorkTask';

/**
 * "You have a decision to make on this record" — shown on a record's own page when an open
 * WorkTask is waiting on the current user.
 *
 * Deliberately entity-agnostic. Everything it needs is on the WorkTask: the outcomes to
 * offer, who it is assigned to, and whether anyone has claimed it. So it is not a
 * plan-version approve button but "the pending decision for this record", which is equally
 * true of an order or anything else the approval engine gates.
 *
 * The coupling that buys is only in the UI: the page composes a TaskManagement view, while
 * the owning module's backend gains no dependency and TaskManagement never learns what the
 * record is. Deciding here goes through the engine — completing the task — rather than
 * around it via the entity's own state command, so the action, the comment and the approval
 * pattern are all recorded exactly as they are from the inbox.
 *
 * No claim step: CompleteTaskAsync accepts PENDING, CLAIMED and ESCALATED and stamps the
 * claim itself when there is none. Claiming stays what it is in the inbox — a courtesy for
 * shared pools — so a task somebody else holds shows who has it instead of a decision.
 */
export interface EntityDecisionBannerProps {
  entityType: string;
  entityId?: string;
  /**
   * Optional "go look at the evidence first" link. The record's own page rarely holds what
   * a decision rests on — a plan version shows metadata, not the budget — so the caller
   * supplies where the numbers live. Kept as a prop rather than a lookup inside the banner:
   * the banner stays entity-agnostic, and only the caller knows what "review" means here.
   */
  reviewHref?: string;
  reviewLabel?: string;
}

function parseOutcomes(availableOutcomes?: string): string[] {
  if (!availableOutcomes) return [];
  try {
    const parsed = JSON.parse(availableOutcomes);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const OPEN_STATUSES = ['PENDING', 'CLAIMED', 'ESCALATED'];

export function EntityDecisionBanner({
  entityType,
  entityId,
  reviewHref,
  reviewLabel,
}: EntityDecisionBannerProps) {
  const { t } = useTranslation();
  const canComplete = useHasPermission('TaskManagement.WorkTask.Complete');
  const { data: tasks } = useEntityTasks(entityType, entityId ?? '');
  const completeMutation = useCompleteTask();

  const [outcome, setOutcome] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const currentUserId = authService.getCurrentUser()?.sub;

  const open = useMemo(() => {
    const items: WorkTask[] = tasks?.items ?? [];
    return items.filter((task) => OPEN_STATUSES.includes(task.status));
  }, [tasks]);

  // Assigned to me, or unassigned and therefore open to whoever holds the permission. A
  // task claimed by somebody else is theirs to finish.
  const mine = open.find(
    (task) =>
      (!task.assignedToUserId || task.assignedToUserId === currentUserId) &&
      (!task.claimedByUserId || task.claimedByUserId === currentUserId),
  );

  // Anything open that is not mine to act on is somebody else's decision, whether they have
  // claimed it or it was simply assigned to them. Keying this on `claimedByUserId` alone left
  // the common case — assigned, not yet claimed — matching neither branch, so the page said
  // nothing at all while every command sat greyed out with no explanation on it.
  const withSomeoneElse = mine ? undefined : open[0];

  if (!entityId || !canComplete) return null;

  if (!mine) {
    if (!withSomeoneElse) return null;
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('Task.Decision.HeldByOther', {
          defaultValue: 'A decision on this record is with {{name}}.',
          name: withSomeoneElse.assignedToUserName ?? '—',
        })}
      </Alert>
    );
  }

  const outcomes = parseOutcomes(mine.availableOutcomes);

  const submit = () => {
    if (!outcome) return;
    setErrors([]);
    completeMutation.mutate(
      { id: mine.id, input: { outcome, comment: comment || undefined } },
      {
        onError: (error: unknown) => {
          const validation = getValidationErrors(error);
          setErrors(
            validation.length > 0
              ? validation
              : [{
                  field: 'form',
                  message: t('Task.Decision.Failed', { defaultValue: 'The decision could not be recorded.' }),
                  instancePath: '#',
                  members: [],
                }],
          );
        },
        onSuccess: () => {
          setOutcome(null);
          setComment('');
        },
      },
    );
  };

  return (
    <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('Task.Decision.Title', { defaultValue: 'Waiting for your decision' })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {mine.title}
      </Typography>

      <ValidationErrorBanner errors={errors} onExpand={() => undefined} onClear={() => setErrors([])} />

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        {outcomes.map((option) => (
          <Chip
            key={option}
            label={t(`Task.Outcome.${option}`, { defaultValue: option })}
            onClick={() => setOutcome(option)}
            color={outcome === option ? 'primary' : 'default'}
            variant={outcome === option ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Stack>

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        placeholder={t('Task.Decision.Comment', { defaultValue: 'Comment (optional)' })}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        sx={{ mb: 1.5 }}
      />

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          size="small"
          disabled={!outcome || completeMutation.isPending}
          onClick={submit}
        >
          {t('Task.Decision.Submit', { defaultValue: 'Record decision' })}
        </Button>
        {reviewHref && (
          <Button variant="text" size="small" component={RouterLink} to={reviewHref}>
            {reviewLabel ?? t('Task.Decision.Review', { defaultValue: 'Review the numbers' })}
          </Button>
        )}
      </Stack>
    </Alert>
  );
}
